import { useEffect, useState } from 'react';
import facebookAvatar from '@/assets/images/avatar-fb.png';
import instagramAvatar from '@/assets/images/avatar-ig.png';
import linkedinAvatar from '@/assets/images/avatar-linkedin.png';
import useWorkflowStore from '@/stores/WorkflowStore';
import { Loader2, MoreHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { cn } from '@/lib/utils';
import { deleteWorkflowInstance, fetchWorkflows } from '@/hooks/workflow';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTableColumnHeader } from '@/components/common/table/data-table-column-header';
import { ManualDataTable } from '@/components/common/table/manual-data-table';

import WorkflowStatusToggle from './WorkflowStatusToggle';
import { WorkflowTableToolbar } from './WorkflowTableToolbar';

// Action cell component to handle state within a React component
const ActionCell = ({ workflow, onRefresh, handleDeleteWorkflow }) => {
  const navigate = useNavigate();
  const [showStatusDialog, setShowStatusDialog] = useState(false);

  return (
    <div className='flex items-center justify-end'>
      {showStatusDialog && (
        <WorkflowStatusToggle
          workflow={workflow}
          onStatusChange={onRefresh}
          onClose={() => setShowStatusDialog(false)}
        />
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='ghost' className='h-8 w-8 p-0'>
            <span className='sr-only'>Open menu</span>
            <MoreHorizontal className='h-4 w-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem
            onClick={() => navigate(`/workflows/edit/${workflow._id}`)}
          >
            Edit
          </DropdownMenuItem>
          {/* Disable status toggle for now */}
          {/* <DropdownMenuItem onClick={() => setShowStatusDialog(true)}>
            Change Status
          </DropdownMenuItem> */}
          <DropdownMenuItem onClick={() => handleDeleteWorkflow(workflow._id)}>
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const WorkflowListPage = () => {
  const navigate = useNavigate();
  const {
    workflowList,
    workflowListLoading,
    setWorkflowList,
    setWorkflowListLoading,
    removeWorkflowFromList,
  } = useWorkflowStore(
    useShallow((state) => ({
      workflowList: state.workflowList,
      workflowListLoading: state.workflowListLoading,
      setWorkflowList: state.setWorkflowList,
      setWorkflowListLoading: state.setWorkflowListLoading,
      removeWorkflowFromList: state.removeWorkflowFromList,
    }))
  );

  const [pagination, setPagination] = useState({
    pageSize: 10,
    pageIndex: 0,
  });

  const [lastPageIndex, setLastPageIndex] = useState(0);
  const [filters, setFilters] = useState({
    name: '',
    status: null,
  });

  // Handle filter changes from the toolbar
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    // Reset to first page when filters change
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const handleDeleteWorkflow = async (workflowInstanceId) => {
    try {
      toast.promise(deleteWorkflowInstance(workflowInstanceId), {
        loading: 'Deleting workflow instance...',
        success: () => {
          removeWorkflowFromList(workflowInstanceId);
          return 'Workflow instance deleted successfully';
        },
        error: 'Error deleting workflow instance',
      });
    } catch (error) {
      toast.error('Error deleting workflow instance:', error);
    }
  };

  // Fetch workflow list with server-side pagination and filtering
  const fetchWorkflowList = async () => {
    try {
      setWorkflowListLoading(true);
      const page = pagination.pageIndex + 1;
      const perPage = pagination.pageSize;

      // Add filter parameters
      const params = {
        page,
        per_page: perPage,
        ...(filters.name && { name: filters.name }),
        ...(filters.status !== null && { is_active: filters.status }),
      };

      const response = await fetchWorkflows(params);
      setWorkflowList(response.data);
      setLastPageIndex(response.data.last_page || 1);
      setWorkflowListLoading(false);
    } catch (error) {
      console.error('Error fetching workflows:', error);
      setWorkflowListLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkflowList();
  }, [pagination.pageIndex, pagination.pageSize, filters]);

  const columns = [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title='Name' />
      ),
      cell: ({ row }) => {
        const workflow = row.original;
        return (
          <div className='flex items-center gap-3'>
            <div
              className='cursor-pointer'
              onClick={() => navigate(`/workflows/edit/${workflow._id}`)}
            >
              <div className='font-semibold'>{workflow.name}</div>
              <div className='line-clamp-1 text-sm text-muted-foreground'>
                {workflow.description}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'is_active',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title='Status' />
      ),
      cell: ({ row }) => {
        const workflow = row.original;
        const status = workflow.is_active ? 'active' : 'inactive';

        return (
          <Badge
            variant='secondary'
            className={cn(
              'capitalize',
              status === 'active' &&
                'bg-green-500/10 text-green-500 hover:bg-green-500/20',
              status === 'inactive' &&
                'bg-gray-500/10 text-gray-500 hover:bg-gray-500/20'
              // For future status types:
              // status === 'pending' && 'bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20',
              // status === 'archived' && 'bg-purple-500/10 text-purple-500 hover:bg-purple-500/20',
              // etc.
            )}
          >
            {status}
          </Badge>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: 'steps',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title='Steps' />
      ),
      cell: ({ row }) => {
        const workflow = row.original;
        return (
          <div className='flex items-center gap-2'>
            <span>{workflow.steps?.length || 0} steps</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'channel',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title='Channels' />
      ),
      cell: ({ row }) => {
        const workflow = row.original;

        if (!workflow.channel || workflow.channel.length === 0) {
          return <span className='text-muted-foreground'>No channels</span>;
        }

        return (
          <div className='flex gap-2'>
            {workflow.channel.map((channel) => (
              <div key={channel} className='flex items-center gap-1'>
                {channel.toLowerCase() === 'instagram' ? (
                  <img
                    src={instagramAvatar}
                    className='h-4 w-4'
                    alt='Instagram'
                  />
                ) : channel.toLowerCase() === 'facebook' ? (
                  <img
                    src={facebookAvatar}
                    className='h-4 w-4'
                    alt='Facebook'
                  />
                ) : channel.toLowerCase() === 'linkedin' ? (
                  <img
                    src={linkedinAvatar}
                    className='h-4 w-4'
                    alt='LinkedIn'
                  />
                ) : null}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: 'triggers',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title='Trigger' />
      ),
      cell: ({ row }) => {
        const workflow = row.original;
        if (workflow.triggers && workflow.triggers.length > 0) {
          return (
            <div className='flex items-center gap-2 text-sm font-medium capitalize'>
              <span>{workflow.triggers[0].type.replaceAll('_', ' ')}</span>
            </div>
          );
        }
        return <span className='text-muted-foreground'>No trigger</span>;
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const workflow = row.original;
        return (
          <ActionCell
            workflow={workflow}
            onRefresh={fetchWorkflowList}
            handleDeleteWorkflow={handleDeleteWorkflow}
          />
        );
      },
    },
  ];

  if (workflowListLoading) {
    return (
      <div className='flex h-[50vh] items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
        <div className='ml-2 text-lg'>Loading workflows...</div>
      </div>
    );
  }

  // Render the header section regardless of whether there are workflows
  const renderHeader = () => (
    <div className='flex justify-between'>
      <div>
        <h3 className='text-xl font-medium'>Workflows</h3>
        <p className='text-sm text-muted-foreground'>
          Manage your automation workflows.
        </p>
      </div>
      <Button onClick={() => navigate('/workflows/create')}>
        Create Workflow
      </Button>
    </div>
  );

  if (!workflowList?.data || workflowList.data.length === 0) {
    return (
      <div className='space-y-6 py-2'>
        {renderHeader()}
        <div className='flex h-[40vh] items-center justify-center'>
          <div className='text-center'>
            <h3 className='text-lg font-semibold'>No workflows found</h3>
            <p className='text-sm text-muted-foreground'>
              Create your first workflow to get started
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6 py-2'>
      {renderHeader()}

      <ManualDataTable
        columns={columns}
        data={workflowList.data}
        hideTheseColumnsInSm={{}}
        // toolbar={(props) => <WorkflowTableToolbar {...props} />}
        pageCount={lastPageIndex}
        onPaginationChange={setPagination}
        pagination={pagination}
        // Yet to implement for server-side filtering in ManualDataTable
        meta={{
          onFilterChange: handleFilterChange,
        }}
      />
    </div>
  );
};

export default WorkflowListPage;
